import React, { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { FeedCardSkeleton } from './Skeleton';
import { Star, Archive, ExternalLink, MessageSquare, AlertCircle } from 'lucide-react';
import { cn, formatDate, getInitials } from '../lib/utils';
import api from '../lib/api';
import { toast } from 'sonner';

export default function ProjectFeedTab({ project }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvents();
  }, [project.id]);

  const loadEvents = async () => {
    try {
      const response = await api.get('/events', { params: { project_id: project.id } });
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  };

  const handleStarToggle = async (eventId, currentStarred) => {
    try {
      await api.put(`/events/${eventId}/status`, { starred: !currentStarred });
      setEvents(events.map(e => 
        e.id === eventId ? { ...e, starred: !currentStarred } : e
      ));
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleArchive = async (eventId) => {
    try {
      await api.put(`/events/${eventId}/status`, { status: 'archived' });
      setEvents(events.filter(e => e.id !== eventId));
      toast.success('Archived');
    } catch (error) {
      toast.error('Failed to archive');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <FeedCardSkeleton />
        <FeedCardSkeleton />
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-border rounded-lg">
        <p className="text-muted-foreground">No activity yet. Connect sources to start seeing updates.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="project-feed-tab">
      {events.map((event) => (
        <div
          key={event.id}
          className={cn(
            'p-4 border rounded-lg bg-card',
            event.status === 'unread' && 'border-l-4 border-l-primary'
          )}
        >
          <div className="flex gap-4">
            <Avatar>
              <AvatarImage src={event.author_avatar} />
              <AvatarFallback>{getInitials(event.author_name || 'U')}</AvatarFallback>
            </Avatar>

            <div className="flex-1 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {event.event_type === 'message' ? (
                      <MessageSquare className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="font-medium">{event.author_name}</span>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-sm text-muted-foreground">{formatDate(event.timestamp)}</span>
                  </div>
                  <h3 className="font-semibold mb-1">{event.title}</h3>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleStarToggle(event.id, event.starred)}
                >
                  <Star className={cn('h-4 w-4', event.starred && 'fill-yellow-400 text-yellow-400')} />
                </Button>
              </div>

              {event.content && (
                <p className="text-sm text-muted-foreground">{event.content}</p>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleArchive(event.id)}
                >
                  <Archive className="h-4 w-4 mr-1" />
                  Archive
                </Button>
                {event.external_url && event.external_url !== '#' && (
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                  >
                    <a href={event.external_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-1" />
                      View
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
